import { MenuItem, Stack, TextField } from '@mui/material'
import type { FormikProps } from 'formik'
import { NumberedSectionCard } from '~/shared/ui'
import type { PolicyEditorValues } from './policyEditorForm'
import {
  usePolicyConfigurationLookups,
  type PolicyConfigurationDetail,
} from './BenefitPolicyService'
interface CoverageDatesSectionProps {
  formik: FormikProps<PolicyEditorValues>
  policy: PolicyConfigurationDetail
  readOnly: boolean
}

/** Section 1: who the policy is for, what it covers, and when. */
export const CoverageDatesSection = ({ formik, policy, readOnly }: CoverageDatesSectionProps) => {
  const lookups = usePolicyConfigurationLookups()
  const { values, handleChange } = formik

  // The resolved alias is what routing actually keys on, so it is shown rather than left to be
  // inferred from the two inputs that produce it.
  const aliasId = policy.plan_mappings.find(
    mapping => mapping.tpa_vendor === values.tpa_vendor,
  )?.alias_id

  const errorFor = (name: keyof PolicyEditorValues) => {
    const error = formik.errors[name]
    return formik.touched[name] && typeof error === 'string' ? error : undefined
  }

  const field = (name: keyof PolicyEditorValues, label: string, type = 'text') => {
    const error = errorFor(name)
    return (
      <TextField
        fullWidth
        required={type === 'date'}
        type={type}
        name={name}
        label={label}
        value={values[name]}
        onChange={handleChange}
        onBlur={formik.handleBlur}
        disabled={readOnly}
        error={!!error}
        helperText={error}
        slotProps={type === 'date' ? { inputLabel: { shrink: true } } : undefined}
        data-testid={`editor-${name}`}
      />
    )
  }

  return (
    <NumberedSectionCard index={1} title="Coverage & dates" testId="section-coverage-dates">
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            fullWidth
            name="contract"
            label="Contract"
            value={values.contract}
            onChange={handleChange}
            disabled={readOnly || lookups.isPending}
            data-testid="editor-contract"
          >
            {(lookups.data?.contracts ?? []).map(contract => (
              <MenuItem key={contract.id} value={contract.id}>
                {contract.name} — {contract.contracted_entity_label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            name="coverage_product"
            label="Coverage product"
            value={values.coverage_product}
            onChange={handleChange}
            disabled={readOnly || lookups.isPending}
            data-testid="editor-coverage-product"
          >
            <MenuItem value="">None</MenuItem>
            {(lookups.data?.coverage_products ?? []).map(product => (
              <MenuItem key={product.id} value={product.id}>
                {product.name}
                {product.payer_name ? ` — ${product.payer_name}` : ''}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction="row" spacing={2}>
          {field('effective_start', 'Effective start', 'date')}
          {field('effective_end', 'Effective end', 'date')}
          {field('electable_start_date', 'Electable start', 'date')}
        </Stack>

        <Stack direction="row" spacing={2}>
          <TextField
            select
            fullWidth
            name="id_card_template"
            label="ID card template"
            value={values.id_card_template}
            onChange={handleChange}
            disabled={readOnly || lookups.isPending}
            data-testid="editor-id-card-template"
          >
            <MenuItem value="">None</MenuItem>
            {(lookups.data?.id_card_templates ?? []).map(choice => (
              <MenuItem key={choice.value} value={choice.value}>
                {choice.display_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            name="accumulator_tiers"
            label="Accumulator tiers"
            value={values.accumulator_tiers}
            onChange={handleChange}
            disabled={readOnly || lookups.isPending}
            data-testid="editor-accumulator-tiers"
          >
            <MenuItem value="">None</MenuItem>
            {/* The lookup types the value as a number while the policy field is a string enum;
                the form speaks the payload's language. */}
            {(lookups.data?.accumulator_tiers ?? []).map(choice => (
              <MenuItem key={choice.value} value={String(choice.value)}>
                {choice.display_name}
              </MenuItem>
            ))}
          </TextField>
          {field('network_id', 'Network ID')}
        </Stack>

        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <TextField
            select
            fullWidth
            name="tpa_vendor"
            label="TPA vendor"
            value={values.tpa_vendor}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            disabled={readOnly || lookups.isPending}
            error={!!errorFor('tpa_vendor')}
            helperText={
              errorFor('tpa_vendor') ?? 'A plan mapping needs a vendor, a group and a plan id.'
            }
            data-testid="editor-tpa-vendor"
          >
            <MenuItem value="">None</MenuItem>
            {(lookups.data?.tpa_vendors ?? []).map(choice => (
              <MenuItem key={choice.value} value={choice.value}>
                {choice.display_name}
              </MenuItem>
            ))}
          </TextField>
          {/* The contract's own group ids, so a mapping cannot be keyed to a group the TPA would
              never send for it — which would leave every member of the plan unrouted. */}
          <TextField
            select
            fullWidth
            name="group_id"
            label="Group ID"
            value={values.group_id}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            disabled={readOnly || policy.contract_group_ids.length === 0}
            error={!!errorFor('group_id')}
            helperText={
              errorFor('group_id') ??
              (policy.contract_group_ids.length === 0
                ? 'This contract lists no group ids yet.'
                : 'The group the TPA sends for this contract.')
            }
            data-testid="editor-group-id"
          >
            <MenuItem value="">None</MenuItem>
            {policy.contract_group_ids.map(groupId => (
              <MenuItem key={groupId} value={groupId}>
                {groupId}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            name="plan_id"
            label="Plan ID"
            value={values.plan_id}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            disabled={readOnly}
            error={!!errorFor('plan_id')}
            helperText={
              errorFor('plan_id') ??
              (aliasId ? `Routes as ${aliasId}` : 'Saved with the plan year appended.')
            }
            data-testid="editor-plan-id"
          />
        </Stack>

        {field('description', 'Description')}
      </Stack>
    </NumberedSectionCard>
  )
}
